import type { ErrorEvent, EventHint } from "@sentry/react";
import { repairConsoleEventTitle } from "@/lib/repairConsoleEventTitle";
import { isTransportDropEvent } from "@/lib/transportDrop";

/**
 * The single `beforeSend` hook `Sentry.init` is given. Issue #1811.
 *
 * Sentry takes exactly one, and there are now two things to do to an event, so
 * this is where their order lives. It is a named, exported, DIRECTLY TESTABLE
 * function on purpose: the alternative was a test asserting that `main.tsx`
 * contains some arrow expression, and a source-text guard passes on a rename
 * and on a destructure that never calls anything. This one is exercised by
 * running it.
 *
 * ── Order, and why ────────────────────────────────────────────────────────
 *
 *   1. **Drop a transport drop.** `reportError` has already recorded it as a
 *      breadcrumb and decided not to page. The only event left is the one
 *      `captureConsoleIntegration` synthesised from its `console.error`, and
 *      per #1255 that is the event that reaches #bugs first.
 *   2. **Otherwise repair the title.** Unchanged #1273 behaviour.
 *
 * The drop goes first because repairing the title of an event about to be
 * discarded is wasted work — not because the two interact. They do not: an
 * event is either tagged or it is not.
 *
 * ⚠️ `repairConsoleEventTitle` is documented and tested as "adds nothing,
 * drops nothing", and that stays literally true. Folding the filter into it
 * would have made its own header comment false, which is how the next reader
 * concludes events cannot disappear here.
 *
 * ⚠️ This is the ONLY place an event is dropped. Anything added here needs the
 * same question answered: what would a real failure look like if this hook ate
 * it? The `8c0b454` blind spot — a caught error reaching nothing — cost seven
 * weeks of #1210 running broken, and a `beforeSend` is the one place that can
 * recreate it for every call site at once.
 */
export function kinBeforeSend(event: ErrorEvent, hint?: EventHint): ErrorEvent | null {
  if (isTransportDropEvent(event)) return null;
  return repairConsoleEventTitle(event, hint);
}
